import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  isLocale,
  type Locale,
  type MessageKey,
  resolveLocale,
  translate,
} from "../../shared/i18n";

const COOKIE = "yc_locale";

function readCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)yc_locale=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function writeCookie(locale: Locale) {
  document.cookie = `${COOKIE}=${locale};path=/;max-age=31536000;samesite=lax`;
}

function detectLocale(): Locale {
  const stored = readCookie();
  if (isLocale(stored)) return stored;
  if (typeof navigator !== "undefined") {
    return resolveLocale(navigator.language.split("-")[0]);
  }
  return "en";
}

export type Translate = (key: MessageKey, params?: Record<string, string | number>) => string;

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translate;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    writeCookie(locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
  }, []);

  const t = useCallback<Translate>((key, params) => translate(locale, key, params), [locale]);

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error("useLocale must be used inside LocaleProvider");
  return value;
}

export function useMessages(): Translate {
  return useLocale().t;
}
